import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
    beteiligungenRecherchieren,
    beteiligungenRecherchierenFuerNamen,
    gesellschaftAusRegisterAnlegen,
    registerBeteiligteVorschau,
    type GesellschaftAnlageErgebnis,
    type RechercheEintrag,
    type RechercheErgebnis,
    type RegisterBeteiligteErgebnis,
    type RegisterBeteiligter,
    type UebernahmeEintrag,
} from "../../services/firmendatenService";
import { baumZeilen } from "./rechercheBaum";

/**
 * Weitere Beteiligungen zu einem Namen — die Frage, die das Register nicht
 * beantwortet.
 *
 * Ein Registerauszug ist nach Rechtsträgern geordnet: Er sagt, wer zu DIESER
 * Gesellschaft gehört, nicht, an welchen anderen dieselbe Person beteiligt ist.
 * Von einer Holding aus führt das Register zu ihrem Gesellschafter und endet
 * dort. Die Suche nach seinem Namen zeigt die übrigen Gesellschaften — und weil
 * das Verzeichnis seine Registerangaben in die Adresse schreibt, sind die
 * meisten davon sofort wieder im Register abrufbar.
 *
 * **Warum ein Baum.** Aus den ersten Treffern wird automatisch weitergesucht.
 * Eine flache Liste verschwiege dann das Wichtigste: „KK PE1 GmbH & Co. KG" hat
 * mit dem eingegebenen Namen nichts zu tun — sie kam über die KK Verwaltungs
 * GmbH dazu. Diese Herkunft ist die eigentliche Auskunft.
 *
 * **Vorgeschlagen wird, entschieden wird vom Anwender.** Ein Namenstreffer ist
 * kein Beleg — „Müller, Aachen" gibt es mehrfach, und die Suche läuft bewusst
 * ohne Anführungszeichen, damit sie auch die Nachbarschaft eines Namens findet.
 * Aus der Trefferliste heraus entsteht deshalb kein Kontakt.
 *
 * **Der Weg zum Kontakt führt über das Register.** Trägt ein Treffer
 * Registernummer UND Gericht, lässt er sich dort nachschlagen: Name, Sitz und
 * die eingetragenen Personen kommen dann aus einer amtlichen Quelle. Erst
 * dieser geprüfte Stand wird — nach Bearbeiten und Bestätigen — angelegt.
 */
const useStyles = makeStyles({
    kopfzeile: { fontSize: "13px", color: "#6b5a66", marginBottom: "12px", lineHeight: 1.5 },
    anfrage: {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "12px",
        color: "#6b5a66",
        backgroundColor: "#f8f4f8",
        ...shorthands.padding("6px", "9px"),
        ...shorthands.borderRadius("7px"),
        marginBottom: "8px",
        wordBreak: "break-all",
    },
    liste: { maxHeight: "48vh", overflowY: "auto", overflowX: "hidden" },
    zweig: { marginTop: "14px" },
    zweigKopf: {
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        color: "#7d2169",
        marginBottom: "5px",
    },
    leer: { fontSize: "12.5px", color: "#8b7a86", ...shorthands.padding("4px", "0") },
    zeile: {
        display: "flex",
        alignItems: "flex-start",
        ...shorthands.gap("10px"),
        ...shorthands.padding("10px", "2px"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
    },
    inhalt: { flexGrow: 1, minWidth: 0 },
    name: {
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "14.5px",
        fontWeight: 700,
        color: "#241522",
        lineHeight: 1.25,
    },
    meta: { display: "block", fontSize: "12.5px", color: "#6b5a66", marginTop: "3px" },
    schnipsel: {
        display: "block",
        fontSize: "12px",
        color: "#8b7a86",
        marginTop: "4px",
        lineHeight: 1.4,
    },
    marke: { fontSize: "11.5px", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 },
    markeVorhanden: { color: "#1f7a4d" },
    markeAbrufbar: { color: "#7d2169" },
    markeOffen: { color: "#a8620a" },
    verweis: { color: "#7d2169", fontWeight: 700, textDecorationLine: "underline" },
    hinweis: {
        fontSize: "12.5px",
        color: "#7a5a18",
        backgroundColor: "#fdf6e3",
        ...shorthands.padding("8px", "10px"),
        ...shorthands.borderRadius("8px"),
        marginTop: "10px",
        lineHeight: 1.45,
    },
    budget: {
        fontSize: "12px",
        color: "#6b5a66",
        marginTop: "12px",
        ...shorthands.borderTop("1px", "solid", "#ece6ec"),
        paddingTop: "8px",
        lineHeight: 1.45,
    },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.gap("8px"),
        ...shorthands.padding("9px", "14px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        backgroundColor: "#fff",
        color: "#7d2169",
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "12.5px",
        fontWeight: 700,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        ":hover": { ...shorthands.borderColor("#cd8fbd"), backgroundColor: "#f8eef5" },
        ":disabled": { color: "#a596a1", cursor: "default", backgroundColor: "#f6f3f5" },
    },
    knopfStark: {
        backgroundColor: "#7d2169",
        color: "#fff",
        ...shorthands.borderColor("#7d2169"),
        ":hover": { backgroundColor: "#591149", ...shorthands.borderColor("#591149") },
        ":disabled": {
            backgroundColor: "#d9c6d4",
            ...shorthands.borderColor("#d9c6d4"),
            color: "#fff",
        },
    },
    knopfKlein: {
        ...shorthands.padding("6px", "10px"),
        fontSize: "11.5px",
        marginTop: "6px",
    },
    formZeile: {
        display: "grid",
        gridTemplateColumns: "minmax(160px, 2fr) minmax(120px, 1fr) minmax(140px, 1fr)",
        ...shorthands.gap("10px"),
        alignItems: "end",
        ...shorthands.padding("10px", "0"),
        ...shorthands.borderBottom("1px", "solid", "#ece6ec"),
        marginBottom: "8px",
        "@media (max-width: 720px)": { gridTemplateColumns: "1fr" },
    },
    feld: { display: "flex", flexDirection: "column", ...shorthands.gap("4px"), minWidth: 0 },
    beschriftung: { fontSize: "11.5px", fontWeight: 700, color: "#6b5a66" },
    eingabe: {
        ...shorthands.padding("9px", "10px"),
        ...shorthands.borderRadius("9px"),
        ...shorthands.border("1.5px", "solid", "#ddd3dc"),
        fontFamily: '"Hanken Grotesk", sans-serif',
        fontSize: "13.5px",
        fontWeight: 600,
        color: "#241522",
        backgroundColor: "#fff",
        minWidth: 0,
        ":focus": { ...shorthands.borderColor("#7d2169"), outlineStyle: "none" },
    },
    haken: { marginTop: "4px" },
    gruppeTitel: {
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#8b7a86",
        marginTop: "6px",
        marginBottom: "4px",
        display: "block",
    },
    ergebnisFehler: { color: "#b42318", marginTop: "8px", marginLeft: "18px" },
    fuss: { display: "flex", ...shorthands.gap("10px"), justifyContent: "flex-end" },
    fehler: { fontSize: "12.5px", color: "#b42318", marginTop: "10px" },
    mitte: { ...shorthands.padding("22px", "0"), textAlign: "center", color: "#6b5a66", fontSize: "13px" },
});

const marke = (t: RechercheEintrag) => {
    if (t.kontaktId) return { text: "Kontakt vorhanden", klasse: "vorhanden" as const };
    if (t.abrufbar) return { text: "im Register abrufbar", klasse: "abrufbar" as const };
    if (t.registernummer) return { text: "Gericht fehlt", klasse: "offen" as const };
    return { text: "nur Verweis", klasse: "offen" as const };
};

/**
 * Ein Registerbeteiligter als Übernahmeeintrag.
 *
 * Bei „aehnlich" wird bewusst NICHT verknüpft: Namensgleichheit ohne
 * Geburtsdatum ist kein Beleg für dieselbe Person — dieselbe Regel wie im
 * Register-Dialog und im Verflechtungslauf.
 */
const eintragAus = (b: RegisterBeteiligter): UebernahmeEintrag => ({
    rolle: b.rolle,
    art: b.art,
    vorname: b.vorname ?? null,
    nachname: b.nachname ?? null,
    geburtsdatum: b.geburtsdatum ?? null,
    bezeichnung: b.bezeichnung ?? null,
    vertretungsbefugnis: b.vertretungsbefugnis ?? null,
    anteil: b.anteil ?? null,
    registerNummer: b.registerNummer ?? null,
    registergericht: b.registergericht ?? null,
    kontaktId: b.abgleich === "vorhanden" ? b.kontaktId ?? null : null,
});

const anzeigename = (b: RegisterBeteiligter) =>
    b.art === "person" ? [b.vorname, b.nachname].filter(Boolean).join(" ") : b.bezeichnung ?? "";

type Schritt = "liste" | "pruefen" | "ergebnis";

export function BeteiligungsrechercheDialog({
    contactId,
    name,
    ort,
    onClose,
    onAngelegt,
}: {
    /** Recherche zu einem Kontakt aus dem Bestand … */
    contactId?: number;
    /** … oder zu einem Namen, den es als Kontakt noch nicht gibt. */
    name?: string;
    ort?: string | null;
    onClose: () => void;
    /** Nach dem Anlegen: Detailansicht neu laden. */
    onAngelegt?: (kontaktId: number) => void;
}) {
    const s = useStyles();
    const [laeuft, setLaeuft] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [ergebnis, setErgebnis] = useState<RechercheErgebnis | null>(null);
    const [schritt, setSchritt] = useState<Schritt>("liste");
    /** Welcher Treffer gerade im Register nachgeschlagen wird. */
    const [holt, setHolt] = useState<string | null>(null);
    /** Läuft gerade die Übernahme? Dann darf das Fenster nicht wegklicken. */
    const [uebernimmt, setUebernimmt] = useState(false);
    /** Der geprüfte Treffer: Suchtreffer plus das, was das Register dazu sagt. */
    const [pruefling, setPruefling] = useState<{
        treffer: RechercheEintrag;
        register: RegisterBeteiligteErgebnis;
    } | null>(null);
    const [firmaName, setFirmaName] = useState("");
    const [nummer, setNummer] = useState("");
    const [gericht, setGericht] = useState("");
    const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set());
    const [angelegt, setAngelegt] = useState<GesellschaftAnlageErgebnis | null>(null);
    const gestartet = useRef(false);
    const offen = useRef(true);

    const suchen = useCallback(async () => {
        setLaeuft(true);
        setFehler(null);
        try {
            const antwort = contactId
                ? await beteiligungenRecherchieren(contactId)
                : await beteiligungenRecherchierenFuerNamen(name ?? "", ort);
            // Der Anwender darf zumachen, während gesucht wird. Danach hier
            // nichts mehr setzen — sonst arbeitet eine geschlossene Ansicht
            // weiter.
            if (offen.current) setErgebnis(antwort);
        } catch (e) {
            if (offen.current) setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            if (offen.current) setLaeuft(false);
        }
    }, [contactId, name, ort]);

    useEffect(() => {
        // Nur einmal starten — im StrictMode läuft der Effekt sonst doppelt.
        // Das Backend fasst zwar beide Aufrufe zu einem Lauf zusammen, aber ein
        // Auftrag kostet bis zu acht Anfragen aus einem geteilten Tagesdeckel:
        // Darauf verlässt sich diese Ansicht nicht.
        if (gestartet.current) return;
        gestartet.current = true;
        void suchen();
    }, [suchen]);

    useEffect(() => () => {
        offen.current = false;
    }, []);

    const klasseFuer = (k: "vorhanden" | "abrufbar" | "offen") =>
        k === "vorhanden" ? s.markeVorhanden : k === "abrufbar" ? s.markeAbrufbar : s.markeOffen;

    /**
     * Den Treffer gegen das Register prüfen — noch ohne etwas anzulegen.
     *
     * Der Endpunkt braucht keinen Kontakt, nur Nummer und Gericht. Was er
     * zurückgibt, ist die amtliche Fassung: Name, Sitz und die eingetragenen
     * Personen. Erst danach entscheidet der Anwender.
     */
    const pruefen = async (t: RechercheEintrag) => {
        setHolt(t.url);
        setFehler(null);
        try {
            const register = await registerBeteiligteVorschau({
                nummer: t.registernummer ?? "",
                registergericht: t.registergericht ?? "",
            });
            if (!offen.current) return;
            setPruefling({ treffer: t, register });
            // Der Registername ist der Rechtsträger; der Suchtreffer trägt oft
            // eine Kurzform. Vorbelegt wird der amtliche, änderbar bleibt er.
            setFirmaName(register.firma?.name || t.name);
            setNummer(t.registernummer ?? "");
            setGericht(t.registergericht ?? "");
            // Vorausgewählt sind die Leitungsorgane — und der Kontakt, um den
            // es hier eigentlich geht: Steht er unter den Beteiligten, ist die
            // Verbindung amtlich belegt und nicht geraten.
            setGewaehlt(
                new Set(
                    register.beteiligte
                        .map((b, i) => ({ b, i }))
                        .filter(
                            ({ b }) =>
                                (b.vorausgewaehlt || (!!contactId && b.kontaktId === contactId)) &&
                                !b.beziehungVorhanden,
                        )
                        .map(({ i }) => i),
                ),
            );
            setSchritt("pruefen");
        } catch (e) {
            if (offen.current) setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            if (offen.current) setHolt(null);
        }
    };

    const umschalten = (i: number) =>
        setGewaehlt((alt) => {
            const neu = new Set(alt);
            if (neu.has(i)) neu.delete(i);
            else neu.add(i);
            return neu;
        });

    const uebernehmen = async () => {
        if (!pruefling) return;
        setUebernimmt(true);
        setFehler(null);
        try {
            const r = await gesellschaftAusRegisterAnlegen({
                name: firmaName.trim(),
                registerNummer: nummer.trim(),
                registergericht: gericht.trim(),
                kontaktId: pruefling.treffer.kontaktId ?? null,
                eintraege: pruefling.register.beteiligte
                    .map((b, i) => ({ b, i }))
                    .filter(({ i }) => gewaehlt.has(i))
                    .map(({ b }) => eintragAus(b)),
            });
            if (!offen.current) return;
            setAngelegt(r);
            setSchritt("ergebnis");
            onAngelegt?.(r.kontaktId);
        } catch (e) {
            if (offen.current) setFehler(e instanceof Error ? e.message : String(e));
        } finally {
            if (offen.current) setUebernimmt(false);
        }
    };

    const zeilen = baumZeilen(ergebnis?.wurzel);

    return createPortal(
        <div className="kvp-rd-scrim" onClick={uebernimmt ? undefined : onClose}>
            <div className="dm" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="dm-head">
                    <div className="dm-head-top">
                        <div className="dm-titles">
                            <div className="dm-kicker">
                                {schritt === "pruefen"
                                    ? "Weitere Beteiligungen · im Register prüfen"
                                    : "Weitere Beteiligungen"}
                            </div>
                            <h2>{ergebnis?.gesucht.name || name || "Recherche"}</h2>
                        </div>
                        {!uebernimmt && (
                            <button className="dm-x" onClick={onClose} aria-label="Schließen">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                <div className="dm-body">
                    {schritt === "liste" && (
                        <>
                            <div className={s.kopfzeile}>
                                Das Handelsregister ist nach Gesellschaften geordnet und kann nicht sagen, an
                                welchen <em>anderen</em> jemand beteiligt ist. Diese Liste stammt aus einer
                                Websuche in einem Registerverzeichnis. Aus den ersten Treffern wird
                                automatisch weitergesucht — zwei Ebenen tief und mit einer festen Obergrenze
                                an Suchanfragen. <strong>Aus dieser Liste heraus wird nichts angelegt</strong>
                                : Ein Namenstreffer ist kein Beleg. Wer eine Gesellschaft übernehmen will,
                                lässt sie erst im Register bestätigen — dafür gibt es den Knopf am Treffer.
                            </div>

                            {laeuft && (
                                <div className={s.mitte}>
                                    Es wird gesucht … Die Weitersuche stellt mehrere Anfragen nacheinander,
                                    das kann bis zu zwei Minuten dauern. Das Fenster darf offen bleiben.
                                </div>
                            )}

                            {!laeuft && ergebnis && (
                                <div className={s.liste}>
                                    {zeilen.map((zeile, zi) => (
                                        <div
                                            key={`${zeile.knoten.anfrage}-${zi}`}
                                            className={s.zweig}
                                            style={{ marginLeft: `${zeile.ebene * 14}px` }}
                                        >
                                            <div className={s.zweigKopf}>
                                                {zeile.ebene === 0 ? "Gesucht" : "Weitergesucht"}:{" "}
                                                {zeile.knoten.gesucht.name}
                                                {zeile.knoten.gesucht.ort ? ` · ${zeile.knoten.gesucht.ort}` : ""}
                                            </div>
                                            <div className={s.anfrage}>{zeile.knoten.anfrage}</div>
                                            {zeile.knoten.treffer.length === 0 && (
                                                <div className={s.leer}>keine neuen Treffer</div>
                                            )}
                                            {zeile.knoten.treffer.map((t, i) => {
                                                const m = marke(t);
                                                return (
                                                    <div key={`${t.url}-${i}`} className={s.zeile}>
                                                        <span className={s.inhalt}>
                                                            <span className={s.name}>{t.name}</span>
                                                            <span className={s.meta}>
                                                                {t.art === "firma" ? "Gesellschaft" : "Person"}
                                                                {t.ort ? ` · ${t.ort}` : ""}
                                                                {t.registernummer ? ` · ${t.registernummer}` : ""}
                                                                {t.registergericht ? ` (AG ${t.registergericht})` : ""}
                                                                {t.kontaktName ? ` · als „${t.kontaktName}" im Bestand` : ""}
                                                                {" · "}
                                                                <a
                                                                    className={s.verweis}
                                                                    href={t.url}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    nachsehen ↗
                                                                </a>
                                                            </span>
                                                            {t.snippet && <span className={s.schnipsel}>{t.snippet}</span>}
                                                            {t.art === "firma" && t.abrufbar && (
                                                                <button
                                                                    type="button"
                                                                    className={mergeClasses(s.knopf, s.knopfKlein)}
                                                                    disabled={holt !== null}
                                                                    onClick={() => void pruefen(t)}
                                                                    title="Nummer und Gericht sind bekannt — die Gesellschaft im Register nachschlagen und danach übernehmen"
                                                                >
                                                                    {holt === t.url
                                                                        ? "Register wird gelesen …"
                                                                        : t.kontaktId
                                                                            ? "Personen aus dem Register ergänzen"
                                                                            : "Im Register prüfen und übernehmen"}
                                                                </button>
                                                            )}
                                                        </span>
                                                        <span className={mergeClasses(s.marke, klasseFuer(m.klasse))}>
                                                            {m.text}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!laeuft && ergebnis && ergebnis.offen.length > 0 && (
                                <div className={s.hinweis}>
                                    Nicht mehr abgefragt: {ergebnis.offen.map((o) => o.name).join(" · ")}
                                </div>
                            )}

                            {!laeuft &&
                                (ergebnis?.hinweise ?? []).map((h, i) => (
                                    <div key={i} className={s.hinweis}>{h}</div>
                                ))}

                            {!laeuft && ergebnis && (
                                <div className={s.budget}>
                                    Diese Recherche hat {ergebnis.anfragen}{" "}
                                    {ergebnis.anfragen === 1 ? "Suchanfrage" : "Suchanfragen"} verbraucht.
                                    Heute insgesamt {ergebnis.budget.verbrauchtHeute} von{" "}
                                    {ergebnis.budget.tagesdeckel} — dieses Kontingent teilt sich mit der
                                    Firmen-Websuche.
                                    {ergebnis.budgetErschoepft
                                        ? " Der Lauf endete am Tageskontingent, nicht an seiner eigenen Grenze."
                                        : ""}
                                </div>
                            )}
                        </>
                    )}

                    {schritt === "pruefen" && pruefling && (
                        <>
                            <div className={s.kopfzeile}>
                                Bitte prüfen und bei Bedarf ändern. Name und Sitz stammen jetzt aus dem
                                Register, nicht mehr aus der Websuche. Angehakt wird nur, wer wirklich
                                mitkommen soll — die Verbindung entsteht dann aus einer amtlichen
                                Eintragung.
                                {pruefling.register.firma?.sitz
                                    ? ` Sitz laut Register: ${pruefling.register.firma.sitz}.`
                                    : ""}
                                {pruefling.treffer.kontaktId
                                    ? ` Diese Gesellschaft gibt es schon als Kontakt („${
                                          pruefling.treffer.kontaktName || pruefling.treffer.name
                                      }") — ihre Stammdaten bleiben unberührt, es kommen nur die
                                      angehakten Personen dazu.`
                                    : ""}
                            </div>
                            {/* Bei einem vorhandenen Kontakt sind die drei Felder gesperrt: An
                                fremden Stammdaten wird hier nichts geändert, und ein Feld, das
                                nichts bewirkt, gehört nicht bedienbar auf den Schirm. */}
                            <div className={s.formZeile}>
                                <span className={s.feld}>
                                    <span className={s.beschriftung}>Name der Gesellschaft</span>
                                    <input
                                        className={s.eingabe}
                                        value={firmaName}
                                        disabled={!!pruefling.treffer.kontaktId}
                                        onChange={(ev) => setFirmaName(ev.target.value)}
                                    />
                                </span>
                                <span className={s.feld}>
                                    <span className={s.beschriftung}>Registernummer</span>
                                    <input
                                        className={s.eingabe}
                                        value={nummer}
                                        disabled={!!pruefling.treffer.kontaktId}
                                        onChange={(ev) => setNummer(ev.target.value)}
                                    />
                                </span>
                                <span className={s.feld}>
                                    <span className={s.beschriftung}>Registergericht</span>
                                    <input
                                        className={s.eingabe}
                                        value={gericht}
                                        disabled={!!pruefling.treffer.kontaktId}
                                        onChange={(ev) => setGericht(ev.target.value)}
                                    />
                                </span>
                            </div>
                            <span className={s.gruppeTitel}>
                                Im Register eingetragen ({pruefling.register.beteiligte.length})
                            </span>
                            <div className={s.liste}>
                                {pruefling.register.beteiligte.map((b, i) => (
                                    <label key={`${i}-${anzeigename(b)}`} className={s.zeile}>
                                        <input
                                            type="checkbox"
                                            className={s.haken}
                                            checked={gewaehlt.has(i)}
                                            disabled={b.beziehungVorhanden}
                                            onChange={() => umschalten(i)}
                                        />
                                        <span className={s.inhalt}>
                                            <span className={s.name}>{anzeigename(b)}</span>
                                            <span className={s.meta}>
                                                {b.rolleAmtlich || b.rolle}
                                                {` · wird verbunden als ${b.beziehungstyp}`}
                                                {b.beziehungsfunktion ? ` (${b.beziehungsfunktion})` : ""}
                                                {b.ort ? ` · ${b.ort}` : ""}
                                                {b.beziehungVorhanden ? " · bereits verbunden" : ""}
                                                {b.abgleich === "aehnlich"
                                                    ? " · namensgleicher Kontakt vorhanden, wird neu angelegt"
                                                    : ""}
                                            </span>
                                        </span>
                                        <span
                                            className={mergeClasses(
                                                s.marke,
                                                b.abgleich === "vorhanden" ? s.markeVorhanden : s.markeAbrufbar,
                                            )}
                                        >
                                            {b.abgleich === "vorhanden" ? "Kontakt vorhanden" : "neu"}
                                        </span>
                                    </label>
                                ))}
                            </div>
                            {(pruefling.register.hinweise ?? []).map((h, i) => (
                                <div key={i} className={s.hinweis}>{h}</div>
                            ))}
                        </>
                    )}

                    {schritt === "ergebnis" && angelegt && (
                        <div className={s.kopfzeile}>
                            <p>
                                „{firmaName}" ist {angelegt.neu ? "angelegt" : "bereits vorhanden"} —{" "}
                                {angelegt.angelegt.length} Personen verbunden
                                {angelegt.angelegt.some((a) => a.neu)
                                    ? ` (${angelegt.angelegt.filter((a) => a.neu).length} neu angelegt)`
                                    : ""}
                                .
                            </p>
                            {angelegt.fehler.length > 0 && (
                                <ul className={s.ergebnisFehler}>
                                    {angelegt.fehler.map((f, i) => (
                                        <li key={i}>{f.name}: {f.grund}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {fehler && <div className={s.fehler}>{fehler}</div>}
                </div>

                <div className={mergeClasses("dm-foot", s.fuss)}>
                    {schritt === "pruefen" && (
                        <>
                            <button
                                type="button"
                                className={s.knopf}
                                disabled={uebernimmt}
                                onClick={() => {
                                    setSchritt("liste");
                                    setPruefling(null);
                                }}
                            >
                                Zurück
                            </button>
                            <button
                                type="button"
                                className={mergeClasses(s.knopf, s.knopfStark)}
                                disabled={
                                    uebernimmt || !firmaName.trim() || !nummer.trim() || !gericht.trim()
                                }
                                onClick={() => void uebernehmen()}
                            >
                                {uebernimmt ? "Wird übernommen …" : "Übernehmen"}
                            </button>
                        </>
                    )}
                    {schritt === "ergebnis" && (
                        <button
                            type="button"
                            className={s.knopf}
                            onClick={() => {
                                setSchritt("liste");
                                setPruefling(null);
                                setAngelegt(null);
                            }}
                        >
                            Weitere übernehmen
                        </button>
                    )}
                    {!uebernimmt && (
                        <button type="button" className={s.knopf} onClick={onClose}>
                            {schritt === "ergebnis" ? "Fertig" : "Schließen"}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
